import {
  AcceptIcon,
  Alert,
  Button,
  ExclamationTriangleIcon,
  Flex,
  Text,
} from "@fluentui/react-northstar";
import FormState, { FormData } from "@shopify/react-form-state";
import { SurveyLimits, TakingCommands, TakingSubmitData, getQuestionFieldId } from "@teams/cards";
import { SurveyDTO } from "@teams/types/api";
import React, { useState } from "react";

import { DropdownField, FieldLabel } from "../../../../../_shared/components/fields";
import { TextAreaField } from "../../../../../_shared/components/fields/TextAreaField";
import { isError } from "../../../../../_shared/services/ChannelApi";
import { surveyApi } from "../../../../../_shared/services/SurveyApi";
import { useTeamsContext } from "../../../../../_shared/state/TeamsContextProvider";

type Props = {
  survey: SurveyDTO;
  trackingSource: string;
  onCompleted: (submitted?: boolean) => void;
};

type SurveyTakingFields = {
  question: string;
  anonymous: boolean;
};

export const QnaQuestionForm: React.FC<Props> = ({ survey, onCompleted, trackingSource }) => {
  const { teamsContext } = useTeamsContext();
  const [submitSuccess, setSubmitSuccess] = useState<boolean>();

  async function save(formFields: FormData<SurveyTakingFields>) {
    const { fields } = formFields;

    const body: TakingSubmitData = {
      command: TakingCommands.Submit,
      surveyId: survey.id as string,
      questionIndexId: survey.questions[0].id,
      questionIds: survey.questions.map(q => q.id),
      direction: "next",
      startDate: new Date().toISOString(),
    };

    body.responseAnonymity = fields.anonymous.value ? "anonymous" : "none";
    body[getQuestionFieldId(survey.questions[0].id)] = fields.question.value;

    const result = await surveyApi.vote(teamsContext, survey.id as string, body, trackingSource);
    if (isError(result)) {
      console.error(result);
      return [result];
    }

    if (!result.missingRequiredIds.length) {
      setSubmitSuccess(true);
      setTimeout(() => {
        onCompleted(true);
      }, 5000);
    }
  }

  if (submitSuccess) {
    return (
      <Alert
        icon={<AcceptIcon />}
        success
        content={<Text content="Question submitted" align="center" />}
        dismissible
        dismissAction={{
          onClick: () => {
            setSubmitSuccess(false);
            onCompleted(true);
          },
        }}
      />
    );
  }

  return (
    <FormState
      initialValues={{ question: "", anonymous: false } as SurveyTakingFields}
      onSubmit={save}
    >
      {({ fields, submit, submitting, errors }) => (
        <>
          {errors?.length > 0 && (
            <Alert
              danger
              icon={<ExclamationTriangleIcon />}
              content={errors.map(e => e.message).join("\r\n")}
            />
          )}

          <Flex column gap="gap.small">
            <TextAreaField
              field={fields.question}
              disabled={submitting}
              fluid
              autoFocus
              rows={4}
              maxLength={SurveyLimits.Questions.OpenEnded.Value.MaxLength}
              placeholder="Type your question"
            />

            <Flex gap="gap.smaller" vAlign="center">
              <FieldLabel label="Send as:" />
              <DropdownField
                fluid
                items={[
                  { label: "Myself", value: false },
                  { label: "Anonymously", value: true },
                ]}
                field={fields.anonymous}
              />
            </Flex>

            <Flex hAlign="end" gap="gap.smaller">
              <Button content="Cancel" disabled={submitting} onClick={() => onCompleted()} />
              <Button
                content="Submit"
                primary
                loading={submitting}
                disabled={!fields.question.value || submitting}
                onClick={() => submit()}
              />
            </Flex>
          </Flex>
        </>
      )}
    </FormState>
  );
};
